// Piano-keyboard input controller + canvas renderer.
//
// Input sources funnel into a single key-tracking map:
//   1. Computer keyboard letter keys mapped to semitones (a/w/s/e/d/f/t/g/y/h/u/j/k/o/l).
//      Toggle via `setQwertyEnabled`.
//   2. Pointer input on the canvas (click and drag) — always active.
//   3. External callers (e.g. Web MIDI) via the public `press`/`release` methods.
//
// State changes fire one of two callbacks:
//   - trigger — a key starts. Synth allocates / retriggers. Includes
//               attack velocity.
//   - release — that specific key ends. Synth releases its voice (or
//               legato-slides in mono mode — engine-side concern).
//
// Each press is independent; the keyboard is polyphonic at the input
// layer. The engine's mode toggle handles whether multiple held notes
// produce multiple voices (poly) or legato-slide a single voice (mono).
// Pointer drags emit `trigger(new)` then `release(old)` so the engine
// sees the new note while the old is still held — that's what triggers
// the legato path in mono mode and produces a brief overlap in poly.
//
// The display is a 9-white-key piano spanning C of the current octave
// through D one octave higher (which covers the entire letter-key mapping
// at any octave). Pressing z/x shifts the octave and re-labels the keys.

/** Semitone offset from C-of-current-octave for each letter key. */
const SEMITONE_BY_LETTER: Readonly<Record<string, number>> = {
  a: 0,
  w: 1,
  s: 2,
  e: 3,
  d: 4,
  f: 5,
  t: 6,
  g: 7,
  y: 8,
  h: 9,
  u: 10,
  j: 11,
  k: 12,
  o: 13,
  l: 14,
};

const LETTER_BY_SEMITONE: Readonly<Record<number, string>> = Object.fromEntries(
  Object.entries(SEMITONE_BY_LETTER).map(([k, v]) => [v, k] as const),
) as Record<number, string>;

/** Semitones (within an octave, relative to C) that are white keys. */
const WHITE_SEMIS = new Set([0, 2, 4, 5, 7, 9, 11]);
/** Semitones that are black keys. */
const BLACK_SEMIS = new Set([1, 3, 6, 8, 10]);

/** Full 88-key piano range. A0 (21) to C8 (108). */
const FIRST_MIDI = 21;
const LAST_MIDI = 108;

/** Precomputed key layout — same regardless of octave/state. */
interface KeyLayout {
  whiteMidis: number[];
  blackMidis: number[];
  /** white_midi → index in whiteMidis (for placing black keys). */
  whiteIndex: Map<number, number>;
}

function buildLayout(): KeyLayout {
  const whiteMidis: number[] = [];
  const blackMidis: number[] = [];
  for (let m = FIRST_MIDI; m <= LAST_MIDI; m++) {
    const semi = m % 12;
    if (WHITE_SEMIS.has(semi)) whiteMidis.push(m);
    else if (BLACK_SEMIS.has(semi)) blackMidis.push(m);
  }
  const whiteIndex = new Map<number, number>();
  whiteMidis.forEach((m, i) => whiteIndex.set(m, i));
  return { whiteMidis, blackMidis, whiteIndex };
}

const LAYOUT = buildLayout();

export interface NoteEvent {
  kind: "trigger" | "release";
  /** MIDI note. Always present. */
  midi: number;
  /** Attack velocity (0..1). Present only on triggers. */
  velocity?: number;
}

export interface PianoKeyboardOptions {
  canvas: HTMLCanvasElement;
  octaveLabel?: HTMLElement;
  initialOctave?: number;
  onNote: (event: NoteEvent) => void;
}

interface HitBox {
  midi: number;
  x: number;
  y: number;
  w: number;
  h: number;
  isBlack: boolean;
}

export class PianoKeyboard {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private octaveLabel: HTMLElement | undefined;
  private onNote: (event: NoteEvent) => void;
  private octave: number;
  private qwertyEnabled = true;

  /** Maps an input id (e.g. "k:a", "p:1", "m:60") to its held MIDI note. */
  private held = new Map<string, number>();
  /** Order of currently-held input ids. Used for releaseAll ordering and
   *  to keep the visual "last-touched" affordance consistent. */
  private holdOrder: string[] = [];

  /** Pointer id → key id, while a pointer is dragging. */
  private pointerHold = new Map<number, string>();

  /** Hit-box cache for pointer hit-testing; rebuilt each draw. */
  private hitBoxes: HitBox[] = [];

  constructor(opts: PianoKeyboardOptions) {
    this.canvas = opts.canvas;
    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("keyboard: 2d context unavailable");
    this.ctx = ctx;
    this.octaveLabel = opts.octaveLabel;
    this.onNote = opts.onNote;
    this.octave = opts.initialOctave ?? 4;

    this.attachKeyboard();
    this.attachPointer();

    // The canvas is often constructed before its containing tree has been
    // appended to the document — in that state `clientWidth/Height` are 0
    // and `draw()` produces a blank zero-sized buffer. A ResizeObserver
    // fires the first time the canvas actually has a layout box and on
    // any subsequent container resize, so we never miss the first paint.
    const ro = new ResizeObserver(() => this.draw());
    ro.observe(this.canvas);

    this.updateOctaveLabel();
    this.draw();
  }

  get currentOctave(): number {
    return this.octave;
  }

  /** Toggle whether QWERTY letter keys produce notes. z/x for octave shift
   *  and the pointer-click input are unaffected. Disabling releases any
   *  letter-key notes that are currently held. */
  setQwertyEnabled(enabled: boolean): void {
    if (this.qwertyEnabled === enabled) return;
    this.qwertyEnabled = enabled;
    if (!enabled) {
      for (const id of [...this.holdOrder]) {
        if (id.startsWith("k:")) this.release(id);
      }
    }
  }

  /** External input sources (MIDI, computer keyboard, pointer) all funnel
   *  here. `id` must be unique per held key; reuse the same id on release.
   *  `velocity` is forwarded on the resulting trigger event (0..1). */
  press(id: string, midi: number, velocity = 1.0): void {
    this.pressInternal(id, midi, velocity);
  }

  release(id: string): void {
    this.releaseInternal(id);
  }

  releaseAll(): void {
    this.releaseAllInternal();
  }

  private semitoneToMidi(semi: number): number {
    return (this.octave + 1) * 12 + semi;
  }

  // ----- input attach -----

  private attachKeyboard(): void {
    window.addEventListener("keydown", (ev) => {
      // Ignore when typing in form fields (no inputs on the test page right
      // now, but defensive for Phase 3+).
      const target = ev.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
        // Allow letter keys to still trigger notes when a slider is focused.
        if (target.tagName === "INPUT" && (target as HTMLInputElement).type !== "range") {
          return;
        }
      }
      if (ev.repeat) return;
      const key = ev.key.toLowerCase();
      if (key === "z") {
        ev.preventDefault();
        this.setOctave(this.octave - 1);
        return;
      }
      if (key === "x") {
        ev.preventDefault();
        this.setOctave(this.octave + 1);
        return;
      }
      if (!this.qwertyEnabled) return;
      const semi = SEMITONE_BY_LETTER[key];
      if (semi === undefined) return;
      ev.preventDefault();
      this.press(`k:${key}`, this.semitoneToMidi(semi));
    });

    window.addEventListener("keyup", (ev) => {
      const key = ev.key.toLowerCase();
      const id = `k:${key}`;
      if (this.held.has(id)) {
        ev.preventDefault();
        this.release(id);
      }
    });

    // Releasing all keys when the window loses focus prevents stuck notes.
    window.addEventListener("blur", () => this.releaseAll());
  }

  private attachPointer(): void {
    this.canvas.addEventListener("pointerdown", (ev) => {
      ev.preventDefault();
      this.canvas.setPointerCapture(ev.pointerId);
      const midi = this.midiAtPoint(ev.offsetX, ev.offsetY);
      if (midi === null) return;
      const id = `p:${ev.pointerId}`;
      this.pointerHold.set(ev.pointerId, id);
      this.press(id, midi);
    });
    this.canvas.addEventListener("pointermove", (ev) => {
      const id = this.pointerHold.get(ev.pointerId);
      if (id === undefined) return;
      const midi = this.midiAtPoint(ev.offsetX, ev.offsetY);
      const currentForPointer = this.held.get(id);
      if (midi === null) {
        // Dragged off the keyboard — release.
        if (currentForPointer !== undefined) this.release(id);
      } else if (midi !== currentForPointer) {
        // Slid to a new key.
        if (currentForPointer !== undefined) {
          this.slidePointer(id, currentForPointer, midi);
        } else {
          this.press(id, midi);
        }
      }
    });
    const endPointer = (ev: PointerEvent): void => {
      const id = this.pointerHold.get(ev.pointerId);
      if (id === undefined) return;
      this.pointerHold.delete(ev.pointerId);
      if (this.held.has(id)) this.release(id);
    };
    this.canvas.addEventListener("pointerup", endPointer);
    this.canvas.addEventListener("pointercancel", endPointer);
    this.canvas.addEventListener("pointerleave", endPointer);
  }

  // ----- press / release engine -----

  private pressInternal(id: string, midi: number, velocity: number): void {
    if (this.held.has(id)) return;
    this.held.set(id, midi);
    this.holdOrder.push(id);
    this.onNote({ kind: "trigger", midi, velocity });
    this.draw();
  }

  private releaseInternal(id: string): void {
    const midi = this.held.get(id);
    if (midi === undefined) return;
    this.held.delete(id);
    const idx = this.holdOrder.indexOf(id);
    if (idx >= 0) this.holdOrder.splice(idx, 1);
    this.onNote({ kind: "release", midi });
    this.draw();
  }

  private releaseAllInternal(): void {
    if (this.holdOrder.length === 0) return;
    // Walk in reverse so the engine's mono-legato stack pops cleanly
    // (each release matches the most-recent held note).
    const ids = [...this.holdOrder].reverse();
    for (const id of ids) {
      const midi = this.held.get(id);
      if (midi !== undefined) this.onNote({ kind: "release", midi });
    }
    this.held.clear();
    this.holdOrder.length = 0;
    this.pointerHold.clear();
    this.draw();
  }

  /** Pointer-drag transition: emit `trigger(new)` *before* `release(prev)`
   *  so the engine's mono allocator sees overlap and runs the legato
   *  path. In poly mode this briefly counts as two voices, which is the
   *  honest representation of a finger sliding from one key to another. */
  private slidePointer(id: string, prevMidi: number, newMidi: number): void {
    this.held.set(id, newMidi);
    this.onNote({ kind: "trigger", midi: newMidi, velocity: 1.0 });
    this.onNote({ kind: "release", midi: prevMidi });
    this.draw();
  }

  private setOctave(o: number): void {
    const clamped = Math.max(0, Math.min(8, o));
    if (clamped === this.octave) return;
    this.octave = clamped;
    // Releasing all keys when the octave shifts avoids "stuck" notes whose
    // letter-key now corresponds to a different MIDI on a future release.
    this.releaseAll();
    this.updateOctaveLabel();
    this.draw();
  }

  private updateOctaveLabel(): void {
    if (this.octaveLabel) {
      this.octaveLabel.textContent = `octave: ${this.octave}`;
    }
  }

  // ----- canvas rendering -----

  private midiAtPoint(x: number, y: number): number | null {
    // Black keys take priority since they overlap white keys.
    for (const k of this.hitBoxes) {
      if (!k.isBlack) continue;
      if (x >= k.x && x < k.x + k.w && y >= k.y && y < k.y + k.h) return k.midi;
    }
    for (const k of this.hitBoxes) {
      if (k.isBlack) continue;
      if (x >= k.x && x < k.x + k.w && y >= k.y && y < k.y + k.h) return k.midi;
    }
    return null;
  }

  /** True if `midi` falls within the QWERTY-playable region for the
   *  current octave (C of curOctave through D of curOctave+1, inclusive). */
  private isInPlayableRegion(midi: number): boolean {
    const base = (this.octave + 1) * 12;
    return midi >= base && midi <= base + 14;
  }

  /** Letter key (a/w/s/...) that maps to `midi` at the current octave, or
   *  undefined if `midi` is outside the playable region. */
  private letterForMidi(midi: number): string | undefined {
    if (!this.isInPlayableRegion(midi)) return undefined;
    const semi = midi - (this.octave + 1) * 12;
    return LETTER_BY_SEMITONE[semi];
  }

  private draw(): void {
    const dpr = window.devicePixelRatio || 1;
    const cssW = this.canvas.clientWidth;
    const cssH = this.canvas.clientHeight;
    if (this.canvas.width !== cssW * dpr || this.canvas.height !== cssH * dpr) {
      this.canvas.width = cssW * dpr;
      this.canvas.height = cssH * dpr;
    }
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    const whiteCount = LAYOUT.whiteMidis.length;
    const whiteW = cssW / whiteCount;
    const whiteH = cssH;
    const blackW = whiteW * 0.62;
    const blackH = cssH * 0.62;

    const heldMidis = new Set(this.held.values());
    this.hitBoxes = [];

    // Playable-region indicator — a subtle band of warmer light *behind*
    // the keys so the playable area reads as a faint highlight in the
    // panel material. Replaces the earlier hard ice-blue underline.
    const playableBase = (this.octave + 1) * 12;
    const playableLastWhite = playableBase + 14;
    const startIdx = LAYOUT.whiteIndex.get(playableBase);
    const endIdx = LAYOUT.whiteIndex.get(playableLastWhite);
    if (startIdx !== undefined && endIdx !== undefined) {
      const px = startIdx * whiteW;
      const pw = (endIdx - startIdx + 1) * whiteW;
      const grad = ctx.createLinearGradient(0, 0, 0, cssH);
      grad.addColorStop(0, "rgba(56, 189, 248, 0.0)");
      grad.addColorStop(0.85, "rgba(56, 189, 248, 0.0)");
      grad.addColorStop(1, "rgba(56, 189, 248, 0.35)");
      ctx.fillStyle = grad;
      ctx.fillRect(px, 0, pw, cssH);
    }

    // White keys — raised panel-material slabs. Each is a vertical gradient
    // (light top → cool bottom) with a 1px top highlight and a thin
    // bottom-right separator so the eye reads adjacent keys as discrete
    // raised pieces.
    for (let i = 0; i < whiteCount; i++) {
      const midi = LAYOUT.whiteMidis[i]!;
      const x = i * whiteW;
      const active = heldMidis.has(midi);
      const inPlayable = this.isInPlayableRegion(midi);

      this.drawWhiteKey(ctx, x, 0, whiteW, whiteH, active, inPlayable);

      // C-note label every octave for orientation.
      if (midi % 12 === 0 && !active) {
        ctx.fillStyle = "rgba(124, 135, 148, 0.85)";
        ctx.font = "9px var(--font-stack), ui-sans-serif, system-ui";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(`C${midi / 12 - 1}`, x + whiteW / 2, whiteH - 22);
      }

      const letter = this.letterForMidi(midi);
      if (letter) {
        ctx.fillStyle = active
          ? "rgba(15, 23, 38, 0.85)"
          : "rgba(124, 135, 148, 0.85)";
        ctx.font = "11px ui-sans-serif, system-ui";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(letter, x + whiteW / 2, whiteH - 8);
      }

      this.hitBoxes.push({ midi, x, y: 0, w: whiteW, h: whiteH, isBlack: false });
    }

    // Black keys — recessed dark slabs sunk into the panel.
    for (const midi of LAYOUT.blackMidis) {
      const whiteBelow = midi - 1;
      const wIdx = LAYOUT.whiteIndex.get(whiteBelow);
      if (wIdx === undefined) continue;
      const x = (wIdx + 1) * whiteW - blackW / 2;
      const active = heldMidis.has(midi);

      this.drawBlackKey(ctx, x, 0, blackW, blackH, active);

      const letter = this.letterForMidi(midi);
      if (letter) {
        ctx.fillStyle = active
          ? "rgba(186, 230, 253, 0.95)"
          : "rgba(203, 210, 219, 0.8)";
        ctx.font = "10px ui-sans-serif, system-ui";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(letter, x + blackW / 2, blackH - 6);
      }

      this.hitBoxes.push({ midi, x, y: 0, w: blackW, h: blackH, isBlack: true });
    }
  }

  /** A single white key. Raised panel material, top-lit. When active, the
   *  key visually depresses 1-2 px and an ice-blue glow leaks out from
   *  underneath ("light bleeding out from the gap when a real key is
   *  pressed" — design.md). */
  private drawWhiteKey(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    active: boolean,
    _inPlayable: boolean,
  ): void {
    const press = active ? 1.5 : 0;
    const keyY = y + press;
    const keyH = h - press;

    // Body gradient — lighter at top, slightly cooler at the bottom so the
    // key reads as catching light from above and seated against the panel.
    const grad = ctx.createLinearGradient(x, keyY, x, keyY + keyH);
    if (active) {
      grad.addColorStop(0, "#dde1ec");
      grad.addColorStop(1, "#c2c8d4");
    } else {
      grad.addColorStop(0, "#fbfdff");
      grad.addColorStop(0.55, "#eef1f6");
      grad.addColorStop(1, "#dee3ec");
    }
    ctx.fillStyle = grad;
    ctx.fillRect(x, keyY, w, keyH);

    // Top highlight — 1 px of near-white catching the panel's light source.
    ctx.fillStyle = active
      ? "rgba(255, 255, 255, 0.35)"
      : "rgba(255, 255, 255, 0.75)";
    ctx.fillRect(x, keyY, w, 1);

    // Right-side separator so adjacent keys read as distinct.
    ctx.fillStyle = "rgba(124, 135, 148, 0.35)";
    ctx.fillRect(x + w - 1, keyY, 1, keyH);

    // Active glow at the bottom — bright ice-blue line + radiating halo.
    if (active) {
      ctx.save();
      ctx.shadowColor = "rgba(56, 189, 248, 1)";
      ctx.shadowBlur = 16;
      ctx.fillStyle = "rgba(125, 211, 252, 0.95)";
      ctx.fillRect(x + 3, keyY + keyH - 3, w - 6, 3);
      ctx.restore();
    }
  }

  /** A single black key. Recessed dark slab sunk into the panel. Pressed
   *  state mirrors the white key — depression + ice-blue glow underneath. */
  private drawBlackKey(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    active: boolean,
  ): void {
    const press = active ? 1.5 : 0;
    const keyY = y + press;
    const keyH = h - press;

    // Subtle inset shadow around the black key well so it reads as
    // recessed below the panel surface (drawn behind the cap).
    ctx.fillStyle = "rgba(8, 12, 20, 0.35)";
    ctx.fillRect(x - 1, y, w + 2, h + 2);

    // Cap — gradient from charcoal at top to deeper black at the bottom.
    const grad = ctx.createLinearGradient(x, keyY, x, keyY + keyH);
    if (active) {
      grad.addColorStop(0, "#1c2230");
      grad.addColorStop(1, "#10141e");
    } else {
      grad.addColorStop(0, "#3a414f");
      grad.addColorStop(1, "#1f2531");
    }
    ctx.fillStyle = grad;
    ctx.fillRect(x, keyY, w, keyH);

    // Top highlight — much fainter than the white key (black material
    // catches less reflected light).
    ctx.fillStyle = "rgba(255, 255, 255, 0.12)";
    ctx.fillRect(x, keyY, w, 1);

    // Active glow underneath.
    if (active) {
      ctx.save();
      ctx.shadowColor = "rgba(56, 189, 248, 1)";
      ctx.shadowBlur = 16;
      ctx.fillStyle = "rgba(125, 211, 252, 0.95)";
      ctx.fillRect(x + 2, keyY + keyH - 3, w - 4, 3);
      ctx.restore();
    }
  }
}
